/**
 * The alpha engine's vocabulary and the pure helpers over its rows —
 * no fetching here; the hooks in api/hooks/alpha.js do that.
 */
const EMPTY = [];

/**
 * The alpha engine's own vocabulary. Horizons are month codes — `1m`, `3m`,
 * `6m`, `12m` — and categories are full SEBI names ("Mid Cap Fund", not
 * "Mid Cap"); anything else is rejected by the resolver. `mfAlphaStatus`
 * publishes both, so the UI reads them rather than guessing.
 */
export const DEFAULT_ALPHA_HORIZON = '12m';
export const ALPHA_HORIZON_LABEL = {
  '1m': '1 month', '3m': '3 months', '6m': '6 months', '12m': '12 months',
};

/** Shortest to longest, which is not the order a string sort produces. */
const HORIZON_ORDER = { '1m': 0, '3m': 1, '6m': 2, '12m': 3, '2y': 4, '5y': 5 };

export const sortHorizons = (list) =>
  [...(list ?? EMPTY)].sort((a, b) => (HORIZON_ORDER[a] ?? 99) - (HORIZON_ORDER[b] ?? 99));

/** Sort ranked rows, or anything else carrying a `horizon`, shortest first. */
export const byHorizon = (a, b) =>
  (HORIZON_ORDER[a?.horizon] ?? 99) - (HORIZON_ORDER[b?.horizon] ?? 99);

/** What each horizon is actually leaning on — the weights differ by design. */
export const HORIZON_BLURB = {
  '1m': 'Short horizon — leans on recent momentum. Decays fastest.',
  '3m': 'Medium horizon — momentum with some persistence.',
  '6m': 'Medium-long horizon — momentum and persistence weighted together.',
  '12m': 'Long horizon — leans on consistency and downside behaviour.',
};

export const BENCHMARK_LABEL = {
  category_average: 'vs category average',
  index_proxy: 'vs market index',
};

export const BENCHMARK_MEANING = {
  category_average:
    'Beat the average fund in its own category. The base rate is ~50% by construction, so only the lift is informative.',
  index_proxy: 'Beat a real, investable market index over the horizon.',
};

/**
 * Per-signal display rules. `unit` decides the formatter and `label` supplies a
 * human name; direction is NOT here on purpose — every row carries the engine's
 * own `higherIsBetter`, and that wins.
 */
const SIGNAL_META = {
  alpha_3m: { label: '3m alpha', unit: 'pct' },
  alpha_12m: { label: '12m alpha', unit: 'pct' },
  consistency: { label: 'Consistency', unit: 'share' },
  information_ratio: { label: 'Information ratio', unit: 'ratio' },
  alpha_5y_reversal: { label: '5y alpha (reversal)', unit: 'pct' },
  downside_capture: { label: 'Downside capture', unit: 'pct' },
};

export const signalLabel = (name) => SIGNAL_META[name]?.label || name;

/**
 * Raw signal values are each in their own units, so they are formatted per
 * signal rather than globally. `consistency` is the only one stored as a 0-1
 * share; everything else off this schema is already a percentage.
 */
export function signalRaw(name, raw) {
  if (raw == null || Number.isNaN(Number(raw))) return '—';
  const unit = SIGNAL_META[name]?.unit || 'ratio';
  const v = Number(raw);
  if (unit === 'pct') return `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(2)}%`;
  if (unit === 'share') return `${(v * 100).toFixed(1)}%`;
  return v.toFixed(2);
}

/**
 * The six signals, in the model's own words. Direction is the part that trips
 * people up: downside capture and the five-year reversal term are GOOD when
 * low, and the reversal term is deliberately the opposite of the twelve-month
 * one — it fights it on purpose.
 */
export const SIGNAL_GLOSSARY = [
  {
    name: 'alpha_3m',
    direction: 'higher',
    measures: 'Cumulative excess return vs benchmark over the last quarter.',
    why: 'The fastest-decaying and strongest short-horizon predictor in the fund momentum literature.',
  },
  {
    name: 'alpha_12m',
    direction: 'higher',
    measures: 'Cumulative excess return vs benchmark over the last year.',
    why: 'The medium-term persistence term.',
  },
  {
    name: 'consistency',
    direction: 'higher',
    measures: 'Share of overlapping rolling 12m windows in which the fund beat its benchmark.',
    why: 'Separates a persistent edge from one lucky quarter — the specific failure the S&P persistence scorecards document.',
  },
  {
    name: 'information_ratio',
    direction: 'higher',
    measures: 'Annualised mean daily excess return ÷ its standard deviation.',
    why: 'Alpha per unit of tracking error; statistically more efficient than raw excess over the same window.',
  },
  {
    name: 'alpha_5y_reversal',
    direction: 'lower',
    measures: 'Five-year cumulative excess return, entered with a NEGATIVE sign.',
    why: 'A mean-reversion term. A fund that has beaten its category for five years has usually grown, drifted from the mandate that earned it, or been lucky.',
  },
  {
    name: 'downside_capture',
    direction: 'lower',
    measures: "The fund's compounded return on the benchmark's DOWN days, as a share of the benchmark's own.",
    why: 'Below 100 means it loses less than the index when the index falls, which compounds over multi-year horizons.',
  },
];

/** The gap between two figures, or null unless both were measured. */
const gap = (a, b) => (a == null || b == null ? null : Number(a) - Number(b));

/**
 * The three cost series a backtest stores, and what each may be compared
 * against.
 *
 * The comparator moves with the series on purpose: an after-tax strategy set
 * beside a pre-tax benchmark is a rigged comparison — buy-and-hold pays LTCG
 * too — so `afterTax` pairs with the taxed category rather than the raw one,
 * and that pairing is the only like-for-like read.
 *
 * UNITS. The engine's `*Excess*` fields are WHOLE-WINDOW totals, not annual
 * rates: on the 2020→2026 run `afterTaxExcessVsCategory` is −31.278, which is
 * 126.921 − 158.199 in total return, while the same gap per year is −2.6.
 * Quoting the stored figure beside two CAGRs reads as an annual number and
 * overstates it twelvefold, so `excessTotal` and `excessCagr` are separate here
 * and every caller has to say which it is showing.
 */
export const COST_SERIES = {
  gross: {
    key: 'gross',
    label: 'Gross',
    note: 'No costs at all. Useful for comparing horizons, never for quoting a return.',
    cagr: (rs) => rs.strategyCagr,
    total: (rs) => rs.strategyTotalReturn,
    catCagr: (rs) => rs.categoryCagr,
    catTotal: (rs) => rs.categoryTotalReturn,
    catLabel: 'Category CAGR',
    excessTotal: (rs) => rs.excessVsCategory,
    excessCagr: (rs) => gap(rs.strategyCagr, rs.categoryCagr),
    indexKey: 'strategyIndex',
  },
  net: {
    key: 'net',
    label: 'Net of exit load',
    note: 'After the 1% exit load on units sold within 365 days. The category comparator pays no load, because buy-and-hold does not trade.',
    cagr: (rs) => rs.strategyNetCagr,
    total: (rs) => rs.strategyNetTotalReturn,
    catCagr: (rs) => rs.categoryCagr,
    catTotal: (rs) => rs.categoryTotalReturn,
    catLabel: 'Category CAGR',
    excessTotal: (rs) => rs.netExcessVsCategory,
    excessCagr: (rs) => gap(rs.strategyNetCagr, rs.categoryCagr),
    indexKey: 'strategyNetIndex',
  },
  afterTax: {
    key: 'afterTax',
    label: 'After load + tax',
    note: 'After exit load and capital gains (STCG 20% under 12m, LTCG 12.5% above), against a category itself taxed once at LTCG.',
    cagr: (rs) => rs.strategyAfterTaxCagr,
    total: (rs) => rs.strategyAfterTaxTotalReturn,
    catCagr: (rs) => rs.categoryAfterTaxCagr,
    catTotal: (rs) => rs.categoryAfterTaxTotalReturn,
    catLabel: 'Category CAGR (taxed)',
    excessTotal: (rs) => rs.afterTaxExcessVsCategory,
    excessCagr: (rs) => gap(rs.strategyAfterTaxCagr, rs.categoryAfterTaxCagr),
    indexKey: 'strategyAfterTaxIndex',
  },
};

/** The same whole-window / per-year split, for one category's own row. */
export const categoryExcess = (row) => ({
  total: row?.excessVsCategory ?? null,
  perYear: gap(row?.strategyCagr, row?.categoryCagr),
});

/** Ranks past this are pooled into one bucket — the tail is thin and noisy. */
const RANK_CAP = 8;

/**
 * Did a BETTER rank produce a BETTER outcome?
 *
 * The load-bearing question, and one a pooled lift cannot answer: if rank 1 and
 * rank 8 earn the same excess then the ordering carries no information even
 * where the lift looks healthy.
 *
 * A stored pick's `excessReturn` is its excess over the REBALANCE PERIOD it was
 * held — a quarter on a quarterly run — not over the signal's horizon. The
 * full-horizon outcome per fund is what produces the hit rate, but the engine
 * aggregates it before answering, so it cannot be broken out here.
 */
export function rankOutcome(picks) {
  const buckets = new Map();
  (picks ?? EMPTY).forEach((p) => {
    if (p.excessReturn == null) return;
    const key = p.rank > RANK_CAP ? RANK_CAP + 1 : p.rank;
    const b = buckets.get(key) || { key, n: 0, sum: 0, beat: 0 };
    b.n += 1;
    b.sum += Number(p.excessReturn);
    if (Number(p.excessReturn) > 0) b.beat += 1;
    buckets.set(key, b);
  });
  return [...buckets.values()]
    .sort((a, b) => a.key - b.key)
    .map((b) => ({
      rank: b.key > RANK_CAP ? `${RANK_CAP + 1}+` : String(b.key),
      n: b.n,
      meanExcess: b.sum / b.n,
      beatShare: (b.beat / b.n) * 100,
    }));
}

/**
 * Share of picks that beat their category, rebalance by rebalance. A model that
 * is right in most periods is a different proposition from one carried by two
 * spectacular quarters, and the pooled lift cannot tell them apart.
 */
export function beatShareByDate(picks) {
  const byDate = new Map();
  (picks ?? EMPTY).forEach((p) => {
    if (p.excessReturn == null) return;
    const b = byDate.get(p.date) || { date: p.date, n: 0, beat: 0, sum: 0 };
    b.n += 1;
    b.sum += Number(p.excessReturn);
    if (Number(p.excessReturn) > 0) b.beat += 1;
    byDate.set(p.date, b);
  });
  return [...byDate.values()]
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    .map((b) => ({
      date: b.date,
      beatShare: (b.beat / b.n) * 100,
      meanExcess: b.sum / b.n,
      n: b.n,
    }));
}

/** One set of picks scored as a whole — how many beat their own category. */
export function scoreOne(picks) {
  const scored = (picks ?? EMPTY).filter((p) => p.excessReturn != null);
  if (!scored.length) return null;
  const beat = scored.filter((p) => Number(p.excessReturn) > 0).length;
  return {
    n: scored.length,
    beat,
    share: (beat / scored.length) * 100,
    meanExcess: scored.reduce((s, p) => s + Number(p.excessReturn), 0) / scored.length,
  };
}

/**
 * Percentile is a *rank* percentile: 0 is the top of the category and 100 the
 * bottom. Reading it the other way round labels the worst funds "top quartile",
 * so every caller goes through here.
 */
export function percentileRank(rankedFund) {
  const p = Number(rankedFund?.percentile);
  if (!Number.isFinite(p)) return null;
  return p <= 1 && p > 0 ? p * 100 : p;
}

/** top | upper | lower | bottom — quartile by rank percentile, best first. */
export function rankQuality(rankedFund) {
  const p = percentileRank(rankedFund);
  if (p == null) return null;
  if (p <= 25) return 'top';
  if (p <= 50) return 'upper';
  if (p <= 75) return 'lower';
  return 'bottom';
}

/**
 * The engine returns one row per benchmark variant, so the same scheme can
 * appear more than once. Keep its best rank.
 */
export function dedupeRanked(rows) {
  const best = new Map();
  (rows ?? EMPTY).forEach((r) => {
    const key = r.securitySymbol;
    if (!key) return;
    const cur = best.get(key);
    if (!cur || Number(r.rank) < Number(cur.rank)) best.set(key, r);
  });
  return [...best.values()].sort((a, b) => Number(a.rank) - Number(b.rank));
}

/**
 * Turns a fund's signal vector into a ranked "what's driving this" list.
 * Contribution is the z-score times the weight the engine gives that signal,
 * signed so a positive number always means it is helping the rank.
 */
export function signalDrivers(rankedFund) {
  return (rankedFund?.signals ?? EMPTY)
    .map((sig) => {
      const z = Number(sig.zscore);
      const w = Number(sig.weight);
      if (!Number.isFinite(z) || !Number.isFinite(w)) return null;
      const direction = sig.higherIsBetter === false ? -1 : 1;
      return {
        name: sig.name,
        description: sig.description,
        raw: Number(sig.raw),
        zscore: z,
        weight: w,
        contribution: z * w * direction,
      };
    })
    .filter(Boolean)
    .sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));
}
